import { Point2 } from '../../../../domain/geometry/geometric-types';
import { kilerMap } from '../../ProductChoices';
import { Diamond } from './wedge-types';

// Tolerance for wedge selection (5cm = 0.05m)
const TOLERANCE = 0.05;

export function kileSelector(
  diamond: Diamond,
  length: number
): string | undefined {
  // Round the input length to 2 decimal places for consistency
  const roundedLength = Math.round(length * 100) / 100;

  // Get all available wedge types with their lengths
  const wedgeTypes = Object.keys(kilerMap)
    .map((key) => ({
      key,
      length: kilerMap[key][0]?.length || 0,
    }))
    .filter((item) => item.length > 0);

  // Sort by length in ascending order
  wedgeTypes.sort((a, b) => a.length - b.length);

  // First try an exact match if available
  const exactMatch = wedgeTypes.find(
    (w) => Math.abs(w.length - roundedLength) < 0.01
  );
  if (exactMatch) {
    return exactMatch.key;
  }

  // Try to find a wedge that's slightly longer (preferred)
  const longerMatch = wedgeTypes.find((w) => w.length >= roundedLength);
  if (longerMatch) {
    return longerMatch.key;
  }

  // Try to find a wedge within tolerance (slightly shorter)
  const toleranceMatch = wedgeTypes
    .reverse()
    .find((w) => roundedLength - w.length <= TOLERANCE);
  if (toleranceMatch) {
    return toleranceMatch.key;
  }

  // If no suitable match found, return the longest available wedge
  if (wedgeTypes.length > 0) {
    const longestWedge = wedgeTypes[0]; // Reverse sorted, so first is longest

    return longestWedge.key;
  }

  return undefined;
}

// Note: This function is also defined in wedge-calculations.ts
// We'll keep it here for backwards compatibility but prefer using the one from wedge-calculations.ts
export function onlyOneSide(diamond?: Diamond) {
  if (!diamond) return false;
  if (!diamond.triangle4 && !diamond.triangle2) return true;
  if (!diamond.triangle1 && !diamond.triangle3) return true;
  return false;
}

export function distanceBetweenPoints(
  point1: Point2,
  point2: Point2,
  unitScale: number
): number {
  return Math.sqrt(
    Math.pow((point2[0] - point1[0]) * unitScale, 2) +
      Math.pow((point2[1] - point1[1]) * unitScale, 2)
  );
}
